/**
 * Reglas de los archivos de evidencia (`attachments`) que acompañan la evaluación
 * de un objetivo o de una competencia.
 *
 * Son las que valida el backend en `objective-reviews/store`,
 * `competency-reviews/store` y sus equivalentes de equipo: hasta
 * {@link MAX_ATTACHMENTS_PER_ITEM} archivos opcionales por ítem, de
 * {@link MAX_ATTACHMENT_SIZE_MB} MB como máximo cada uno y solo con las
 * extensiones de {@link ALLOWED_ATTACHMENT_EXTENSIONS}. Se centralizan aquí para
 * validar en el front lo mismo que el servidor: si un archivo no cumple, la
 * petición completa (todos los objetivos/competencias del envío) se rechaza, así
 * que conviene frenarlo al momento de adjuntarlo.
 *
 * Aquí vive además la normalización de las evidencias **ya guardadas** que el
 * backend devuelve al recuperar una evaluación realizada ({@link ApiAttachment} →
 * {@link PerformanceAttachment}).
 */

// Solo el tipo: el import se borra al compilar, así que no genera ciclo en runtime
// con el modelo (que a su vez importa `PerformanceAttachmentKind` de este archivo).
import type { PerformanceAttachment } from "./proceso-desempeno.model";

/** Máximo de archivos de evidencia por objetivo/competencia. */
export const MAX_ATTACHMENTS_PER_ITEM = 5;

/** Peso máximo de cada archivo, en MB. */
export const MAX_ATTACHMENT_SIZE_MB = 10;

/** Peso máximo de cada archivo, en bytes. */
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

/**
 * Extensiones aceptadas por el backend: imágenes, documentos ofimáticos y video
 * (el equipo backend confirmó que el video y el `heic` también se aceptan; la
 * documentación inicial no los listaba). `webm` es el formato que produce el
 * grabador in-browser, y `mov`/`heic` los que entrega iOS desde su cámara y su
 * galería.
 */
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
	"jpg",
	"jpeg",
	"png",
	"heic",
	"pdf",
	"doc",
	"docx",
	"xls",
	"xlsx",
	"mp4",
	"mov",
	"webm",
] as const;

/**
 * MIME types de las extensiones aceptadas (para el `accept` y la validación).
 *
 * Los de video espejan {@link VideoUploadService.validateVideoType} de checklist
 * —la vista que ya adjunta fotos/videos desde web, Android e iOS—, incluidos los
 * no estándar que reportan algunos dispositivos: `video/mov` (webview Android) y
 * `image/jpg`. Sin ellos, un archivo válido llegaría con un `type` que no está en
 * la lista y se rechazaría por error.
 */
const ALLOWED_ATTACHMENT_MIME_TYPES = [
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/heic",
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"video/mp4",
	"video/mov",
	"video/quicktime",
	"video/webm",
];

/**
 * Valor del `accept` del input de archivos. Lleva extensiones **y** MIME types: en
 * escritorio el filtro se aplica por MIME y en algunos webviews de Android solo se
 * respetan las extensiones.
 */
export const ATTACHMENT_ACCEPT = [
	...ALLOWED_ATTACHMENT_EXTENSIONS.map((extension) => `.${extension}`),
	...ALLOWED_ATTACHMENT_MIME_TYPES,
].join(",");

/** Etiqueta legible de los formatos aceptados ("JPG, PNG, PDF, DOC…"). */
export const ALLOWED_ATTACHMENT_LABEL = ALLOWED_ATTACHMENT_EXTENSIONS.map((extension) =>
	extension.toUpperCase(),
).join(", ");

/** Tipo de adjunto para el render (miniatura vs ícono por formato). */
export type PerformanceAttachmentKind =
	| "image"
	| "video"
	| "pdf"
	| "word"
	| "excel"
	| "file";

/** Extensión en minúsculas de un nombre de archivo ("informe.PDF" → "pdf"). */
export function attachmentExtension(fileName: string): string {
	const parts = (fileName || "").split(".");
	return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
}

/**
 * ¿El archivo tiene una extensión **o** un MIME que el backend acepta?
 *
 * Basta con que cumpla uno de los dos, igual que en checklist (que valida por
 * MIME y cae a la extensión para el `.heic` de iOS): en el webview de iOS/Android
 * el `type` de un archivo elegido de galería llega a veces vacío o con un valor
 * no estándar, y al revés, una captura de cámara puede llegar sin extensión en el
 * nombre. Exigir ambos rechazaría archivos perfectamente válidos.
 */
export function isAllowedAttachment(file: File): boolean {
	const extension = attachmentExtension(file?.name ?? "");
	if ((ALLOWED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(extension)) return true;
	return ALLOWED_ATTACHMENT_MIME_TYPES.includes(file?.type ?? "");
}

/** ¿El archivo respeta el peso máximo permitido? */
export function isWithinAttachmentSize(file: File): boolean {
	return (file?.size ?? 0) <= MAX_ATTACHMENT_SIZE_BYTES;
}

/** Clasifica el archivo para elegir su ícono/miniatura en la lista de adjuntos. */
export function attachmentKindFor(file: File): PerformanceAttachmentKind {
	return attachmentKindOf(file?.name ?? "", file?.type ?? "");
}

/**
 * Misma clasificación que {@link attachmentKindFor} pero a partir del nombre y el
 * MIME sueltos: los adjuntos que devuelve el backend (evaluación ya realizada) no
 * son un `File`, solo su metadata.
 */
export function attachmentKindOf(
	fileName: string,
	mimeType: string,
): PerformanceAttachmentKind {
	const extension = attachmentExtension(fileName ?? "");
	const mime = mimeType ?? "";
	if (
		extension === "jpg" ||
		extension === "jpeg" ||
		extension === "png" ||
		extension === "heic"
	) {
		return "image";
	}
	if (mime.startsWith("image/")) return "image";
	if (extension === "mp4" || extension === "mov" || extension === "webm") return "video";
	if (mime.startsWith("video/")) return "video";
	if (extension === "pdf" || mime === "application/pdf") return "pdf";
	if (extension === "doc" || extension === "docx") return "word";
	if (extension === "xls" || extension === "xlsx") return "excel";
	return "file";
}

/**
 * Evidencia **ya guardada** tal como la devuelve el backend dentro de cada ítem de
 * una evaluación ya realizada (`attachments[]` de `competency-reviews/show`,
 * `objective-reviews/show` y sus equivalentes de equipo).
 *
 * Ojo: `size` **no viene en bytes** (llega en KB, ej. `51` para "51 KB"), por eso
 * el peso a mostrar es `size_label`, que el backend ya entrega formateado.
 */
export interface ApiAttachment {
	id: number | string;
	/** Nombre original del archivo ("evidencia_ventas.pdf"). */
	name: string;
	/** URL pública del archivo almacenado. */
	url: string;
	mime: string | null;
	size: number | null;
	/** Peso ya formateado por el backend ("51 KB"). */
	size_label: string | null;
	uploaded_at?: string | null;
}

/**
 * Normaliza una evidencia ya guardada a la forma que consume la vista
 * ({@link PerformanceAttachment}). El adjunto queda **sin `file`**: no se puede
 * reconstruir el binario desde la URL, y tampoco hace falta —solo se muestra y se
 * abre—. Su `previewUrl` es la URL remota, así que se pinta igual que un archivo
 * recién elegido sin tratamiento especial en el componente.
 */
export function mapApiAttachment(raw: ApiAttachment): PerformanceAttachment {
	const mimeType = raw?.mime ?? "";
	return {
		id: raw?.id,
		name: raw?.name ?? "",
		mimeType,
		kind: attachmentKindOf(raw?.name ?? "", mimeType),
		size: 0,
		sizeLabel: raw?.size_label ?? "",
		previewUrl: raw?.url ?? "",
	};
}

/** Normaliza la lista `attachments[]` de un ítem (tolera `null`/ausente). */
export function mapApiAttachments(raw: ApiAttachment[] | null | undefined): PerformanceAttachment[] {
	return (raw ?? []).map(mapApiAttachment);
}

/**
 * Los archivos **nuevos** de la lista, listos para el payload. Los adjuntos ya
 * guardados no traen `file` (su binario vive en el backend), así que quedan fuera:
 * al reenviar solo viajan los recién elegidos.
 *
 * Devuelve `undefined` —no `[]`— a propósito: así la llave `attachments` se omite
 * del cuerpo y el envío sigue siendo JSON en vez de `multipart/form-data`.
 */
export function buildAttachmentsPayload(
	attachments: PerformanceAttachment[] | null | undefined,
): File[] | undefined {
	const files = (attachments ?? [])
		.map((attachment) => attachment.file)
		.filter((file): file is File => !!file);
	return files.length ? files : undefined;
}

/**
 * IDs de las evidencias **ya guardadas** que el usuario quitó de la lista, para la
 * llave `remove_attachment_ids` de los endpoints que la aceptan (hoy, el seguimiento
 * de objetivos). Solo mira los adjuntos con `id` —los nuevos no lo tienen— y, como
 * {@link buildAttachmentsPayload}, devuelve `undefined` cuando no hay ninguno.
 */
export function removedAttachmentIds(
	original: PerformanceAttachment[] | null | undefined,
	current: PerformanceAttachment[] | null | undefined,
): (number | string)[] | undefined {
	const kept = new Set((current ?? []).map((attachment) => String(attachment.id)));
	const removed = (original ?? [])
		.filter((attachment) => attachment.id != null && !kept.has(String(attachment.id)))
		.map((attachment) => attachment.id);
	return removed.length ? removed : undefined;
}

/** Peso del archivo en texto corto ("820 KB", "3.4 MB"). */
export function formatAttachmentSize(bytes: number): string {
	if (!bytes || bytes < 0) return "";
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${Math.round(kb)} KB`;
	return `${Math.round((kb / 1024) * 10) / 10} MB`;
}
